import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fetchWithAuth, type PortalActivityItem, type PortalActivityFiled } from "@/lib/api";
import { useUserPref, PREF_PORTAL_RESPONSES_OLDEST_FIRST } from "@/lib/userPrefs";
import { Layout } from "@/components/layout/Layout";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { triggerDownload } from "@/lib/download";
import {
  Eye, Download, ExternalLink, Paperclip, MessageSquare, Check, ArrowDown, ArrowUp, FolderOpen, Search, FileText,
} from "lucide-react";

/**
 * Portal Responses (/client-responses).
 *
 * v1.5.84: the Documents tab is the firm's incoming-documents triage queue. Portal task
 * uploads and document-request link uploads both land here; staff file each one by hand
 * (CentCom folder, SmartVault, wherever it belongs) and then mark the row filed. The filed
 * stamp is FIRM-WIDE (migration 116): one person filing it clears it for everyone. Client
 * comments live on their own tab and keep the per-user read dots (migration 099).
 *
 * One query feeds both tabs: GET /api/portal-activity?include=documents returns every item
 * with its `filed` stamp, and the page partitions client-side so the pill counts are free.
 */

// The page's own cache entry. Writes invalidate the whole ["portal-activity"] prefix so the
// account Portal tab, the Overview card and the Projects chips refresh too.
const PAGE_KEY = ["portal-activity", "page"] as const;

type FilableItem = PortalActivityItem & { type: "upload" | "document" };
const isFilable = (i: PortalActivityItem): i is FilableItem => i.type === "upload" || i.type === "document";
const itemKey = (i: PortalActivityItem) => `${i.type}:${i.id}`;

const IMAGE_NAME = /\.(jpe?g|png|gif|webp|heic|bmp)$/i;

const isPreviewable = (item: PortalActivityItem) => {
  const t = (item.file_type ?? "").toLowerCase();
  const n = (item.name ?? "").toLowerCase();
  return (item.type === "upload" || item.type === "document") && (
    t === "application/pdf" || n.endsWith(".pdf") ||
    t.startsWith("image/") || IMAGE_NAME.test(n)
  );
};

const formatRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return null;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

function ProjectBadge({ type, taxYear }: { type?: string | null; taxYear?: string | number | null }) {
  const parts: string[] = [];
  if (type) parts.push(type);
  if (taxYear) parts.push(String(taxYear));
  if (!parts.length) return null;
  return (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200 shrink-0">
      {parts.join(" · ")}
    </span>
  );
}

function useRefetchPortalActivity() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["portal-activity"] });
    queryClient.invalidateQueries({ queryKey: ["portal-activity-accounts"] });
  };
}

type OpenPreview = (url: string, name: string) => void;

/* === DOCUMENTS TAB === */

type StatusPill = "open" | "filed" | "all";
type SourceFilter = "all" | "upload" | "document";

const STATUS_PILLS: { value: StatusPill; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "filed", label: "Filed" },
  { value: "all", label: "All" },
];

const inputClass =
  "text-sm border border-border rounded-md px-2.5 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30";

function FiledChip({ filed, onUnfile }: { filed: PortalActivityFiled; onUnfile: () => void }) {
  return (
    <div className="space-y-0.5">
      <span
        className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 whitespace-nowrap"
        title={new Date(filed.at).toLocaleString()}
      >
        <Check className="w-3 h-3" />
        Filed · {filed.by_name ?? "Staff"} · {formatRelative(filed.at)}
      </span>
      {filed.note && <p className="text-xs text-muted-foreground break-words">{filed.note}</p>}
      <button onClick={onUnfile} className="text-xs font-medium text-primary hover:underline">
        Unfile
      </button>
    </div>
  );
}

function DocumentsTab({
  items,
  onPreview,
  onCountChange,
}: {
  items: PortalActivityItem[];
  onPreview: OpenPreview;
  onCountChange: (n: number) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const refetchPortalActivity = useRefetchPortalActivity();

  const [status, setStatus] = useState<StatusPill>("open");
  const [accountFilter, setAccountFilter] = useState<string>("all");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [search, setSearch] = useState("");
  const [oldestFirst, setOldestFirst] = useUserPref(PREF_PORTAL_RESPONSES_OLDEST_FIRST, false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fileTargets, setFileTargets] = useState<FilableItem[] | null>(null);
  const [fileIsBulk, setFileIsBulk] = useState(false);
  const [note, setNote] = useState("");

  // Selection never survives a change of what is on screen.
  const resetSelection = () => setSelected(new Set());

  const docs = useMemo(() => items.filter(isFilable), [items]);

  const accounts = useMemo(() => {
    const map = new Map<string, string>();
    docs.forEach((i) => map.set(i.account_id, i.account_name));
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [docs]);

  const years = useMemo(() => {
    const set = new Set<string>();
    docs.forEach((i) => { if (i.project_tax_year) set.add(String(i.project_tax_year)); });
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [docs]);

  // Every filter except the status pill; the pill counts are computed from this.
  const filteredNoStatus = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((i) =>
      (accountFilter === "all" || i.account_id === accountFilter) &&
      (yearFilter === "all" || String(i.project_tax_year ?? "") === yearFilter) &&
      (sourceFilter === "all" || i.type === sourceFilter) &&
      (!q || (i.name ?? "").toLowerCase().includes(q))
    );
  }, [docs, accountFilter, yearFilter, sourceFilter, search]);

  const counts = useMemo(() => {
    const filedCount = filteredNoStatus.filter((i) => !!i.filed).length;
    return { open: filteredNoStatus.length - filedCount, filed: filedCount, all: filteredNoStatus.length };
  }, [filteredNoStatus]);

  const visible = useMemo(() => {
    const rows = filteredNoStatus.filter((i) =>
      status === "all" ? true : status === "filed" ? !!i.filed : !i.filed
    );
    const dir = oldestFirst ? 1 : -1;
    return [...rows].sort((a, b) => dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
  }, [filteredNoStatus, status, oldestFirst]);

  useEffect(() => { onCountChange(visible.length); }, [visible.length, onCountChange]);

  // Only open rows can be selected; the count shown is the part of the selection still on screen.
  const selectable = useMemo(() => visible.filter((i) => !i.filed), [visible]);
  const visibleSelected = useMemo(() => selectable.filter((i) => selected.has(itemKey(i))), [selectable, selected]);
  const allChecked = selectable.length > 0 && visibleSelected.length === selectable.length;

  const toggleOne = (item: FilableItem) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = itemKey(item);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(selectable.map(itemKey)));

  const patchItems = (keys: Set<string>, patch: Partial<PortalActivityItem>) => {
    queryClient.setQueryData<PortalActivityItem[]>(PAGE_KEY, (prev) =>
      prev?.map((i) => (keys.has(itemKey(i)) ? { ...i, ...patch } : i))
    );
  };

  // Per-user "I looked" receipt, silent. Document-request items have no receipts on the
  // server (the 099 CHECK only allows upload|comment), so they must never reach this call.
  const markUploadRead = async (item: PortalActivityItem) => {
    if (item.type !== "upload" || item.read) return;
    patchItems(new Set([itemKey(item)]), { read: true });
    try {
      await api.markPortalActivityRead([{ type: "upload", id: item.id, account_id: item.account_id }]);
    } catch { /* silent: there is no read dot on this tab */ }
    refetchPortalActivity();
  };

  const signedUrl = async (item: FilableItem, mode: "view" | "download"): Promise<string> => {
    if (item.type === "document") {
      // This endpoint answers { downloadUrl }, not { url } like the task-attachment one.
      const path = mode === "view"
        ? `/api/accounts/${item.account_id}/documents/${item.id}/download-url?mode=view`
        : `/api/accounts/${item.account_id}/documents/${item.id}/download-url?mode=download`;
      const { downloadUrl } = await fetchWithAuth<{ downloadUrl: string }>(path);
      return downloadUrl;
    }
    if (!item.task_id) throw new Error("upload has no task");
    // Without ?mode=download this route returns the inline URL the Preview dialog renders,
    // so a "download" of a PDF just opened it in a tab (ENG-109).
    const qs = mode === "download" ? "?mode=download" : "";
    const { url } = await fetchWithAuth<{ url: string }>(`/api/project-tasks/${item.task_id}/attachments/${item.id}/download${qs}`);
    return url;
  };

  const preview = async (item: FilableItem) => {
    try {
      onPreview(await signedUrl(item, "view"), item.name);
      void markUploadRead(item);
    } catch {
      toast({ title: "Preview failed", variant: "destructive" });
    }
  };

  const download = async (item: FilableItem) => {
    try {
      triggerDownload(await signedUrl(item, "download"), item.name);
      void markUploadRead(item);
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  const goToTask = async (e: React.MouseEvent<HTMLAnchorElement>, item: FilableItem) => {
    if (item.read) return;
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href")!;
    await markUploadRead(item);
    window.location.href = href;
  };

  const openFileDialog = (targets: FilableItem[], bulk: boolean) => {
    if (!targets.length) return;
    setNote("");
    setFileIsBulk(bulk);
    setFileTargets(targets);
  };

  const closeFileDialog = () => {
    setFileTargets(null);
    setNote("");
  };

  const confirmFile = async () => {
    const targets = fileTargets ?? [];
    if (!targets.length) return;
    const trimmed = note.trim();
    const bulk = fileIsBulk;
    patchItems(new Set(targets.map(itemKey)), {
      filed: { at: new Date().toISOString(), by_user_id: null, by_name: "You", note: trimmed || null },
    });
    closeFileDialog();
    try {
      await api.markPortalActivityFiled(
        targets.map((i) => ({ type: i.type, id: i.id, account_id: i.account_id })),
        trimmed || undefined,
      );
      if (bulk) resetSelection();
    } catch {
      toast({ title: "Couldn't mark as filed", variant: "destructive" });
    }
    refetchPortalActivity();
  };

  const unfile = async (item: FilableItem) => {
    patchItems(new Set([itemKey(item)]), { filed: null });
    try {
      await api.unfilePortalActivity(item.type, item.id);
    } catch {
      toast({ title: "Couldn't unfile", variant: "destructive" });
    }
    refetchPortalActivity();
  };

  const emptyText =
    status === "open" ? "Nothing waiting to be filed."
    : status === "filed" ? "Nothing filed yet."
    : "No client portal activity yet.";

  return (
    <div>
      {/* Status pills + sort */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {STATUS_PILLS.map((p) => (
          <button
            key={p.value}
            onClick={() => { setStatus(p.value); resetSelection(); }}
            className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
              status === p.value
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-foreground border-border hover:bg-muted"
            }`}
          >
            {p.label} ({counts[p.value]})
          </button>
        ))}
        <button
          onClick={() => setOldestFirst(!oldestFirst)}
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm border border-border bg-background hover:bg-muted transition-colors"
          title="Change sort order"
        >
          {oldestFirst ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
          {oldestFirst ? "Oldest first" : "Newest first"}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={accountFilter}
          onChange={(e) => { setAccountFilter(e.target.value); resetSelection(); }}
          className={`${inputClass} min-w-[200px]`}
          aria-label="Client"
        >
          <option value="all">All clients</option>
          {accounts.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select
          value={yearFilter}
          onChange={(e) => { setYearFilter(e.target.value); resetSelection(); }}
          className={inputClass}
          aria-label="Tax year"
        >
          <option value="all">All years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value as SourceFilter); resetSelection(); }}
          className={inputClass}
          aria-label="Source"
        >
          <option value="all">All sources</option>
          <option value="upload">Portal task upload</option>
          <option value="document">Document request</option>
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetSelection(); }}
            placeholder="Search file names"
            className={`${inputClass} w-full pl-8`}
          />
        </div>
      </div>

      {/* Bulk bar */}
      {visibleSelected.length > 0 && (
        <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-md border border-primary/30 bg-primary/5">
          <span className="text-sm font-medium text-foreground">{visibleSelected.length} selected</span>
          <button
            onClick={() => openFileDialog(visibleSelected, true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors"
          >
            <Check className="w-3 h-3" />
            Mark filed…
          </button>
          <button onClick={resetSelection} className="text-xs font-medium text-muted-foreground hover:underline">
            Clear
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm font-medium text-foreground">{emptyText}</p>
          {status === "all" && (
            <p className="text-xs text-muted-foreground mt-1">Uploads and document-request files from clients will appear here.</p>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-x-auto bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground uppercase tracking-wide">
              <tr className="text-left">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    disabled={selectable.length === 0}
                    onChange={toggleAll}
                    aria-label="Select all open rows"
                    className="accent-primary"
                  />
                </th>
                <th className="px-3 py-2 font-semibold">Client</th>
                <th className="px-3 py-2 font-semibold">Project</th>
                <th className="px-3 py-2 font-semibold">Task / request</th>
                <th className="px-3 py-2 font-semibold">File</th>
                <th className="px-3 py-2 font-semibold">Received</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.map((item) => {
                const size = formatFileSize(item.file_size);
                const isDoc = item.type === "document";
                const taskOrRequest = (isDoc ? item.request_title : item.task_title) ?? null;
                return (
                  <tr key={itemKey(item)} className="align-top hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2.5">
                      {!item.filed && (
                        <input
                          type="checkbox"
                          checked={selected.has(itemKey(item))}
                          onChange={() => toggleOne(item)}
                          aria-label={`Select ${item.name}`}
                          className="accent-primary"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-medium text-foreground max-w-[160px]">
                      <a href={`/accounts/${item.account_id}`} className="hover:underline line-clamp-2" title={item.account_name}>
                        {item.account_name}
                      </a>
                    </td>
                    <td className="px-3 py-2.5 max-w-[180px]">
                      {isDoc ? (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200 whitespace-nowrap">
                          Document request
                        </span>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-foreground/90 line-clamp-2" title={item.project_title ?? ""}>{item.project_title}</p>
                          <ProjectBadge type={item.project_type} taxYear={item.project_tax_year} />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-[180px] text-foreground/90">
                      <span className="line-clamp-2" title={taskOrRequest ?? ""}>{taskOrRequest ?? "—"}</span>
                    </td>
                    <td className="px-3 py-2.5 max-w-[220px]">
                      <div className="flex items-start gap-1.5">
                        {isDoc
                          ? <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                          : <Paperclip className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />}
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate" title={item.name}>{item.name}</p>
                          {size && <p className="text-xs text-muted-foreground">{size}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground" title={new Date(item.created_at).toLocaleString()}>
                      {formatRelative(item.created_at)}
                    </td>
                    <td className="px-3 py-2.5 max-w-[220px]">
                      {item.filed ? (
                        <FiledChip filed={item.filed} onUnfile={() => unfile(item)} />
                      ) : (
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">Open</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        {isPreviewable(item) && (
                          <button
                            onClick={() => preview(item)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                          >
                            <Eye className="w-3 h-3" />
                            Preview
                          </button>
                        )}
                        <button
                          onClick={() => download(item)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted text-foreground text-xs font-medium hover:bg-muted/80 transition-colors border border-border"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </button>
                        {isDoc ? (
                          <a
                            href={`/accounts/${item.account_id}?tab=documents`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
                          >
                            <FolderOpen className="w-3 h-3" />
                            Open Documents
                          </a>
                        ) : (
                          <a
                            href={`/accounts/${item.account_id}?openProject=${item.project_id ?? ""}&openTask=${item.task_id ?? ""}`}
                            onClick={(e) => goToTask(e, item)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Go to task
                          </a>
                        )}
                        {!item.filed && (
                          <button
                            onClick={() => openFileDialog([item], false)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap"
                          >
                            <Check className="w-3 h-3" />
                            Mark filed
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mark filed dialog (one row or a bulk selection) */}
      <Dialog open={fileTargets !== null} onOpenChange={(open) => { if (!open) closeFileDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              {fileTargets && fileTargets.length > 1
                ? `Mark ${fileTargets.length} documents filed`
                : "Mark document filed"}
            </DialogTitle>
          </DialogHeader>
          {fileTargets && fileTargets.length === 1 && (
            <p className="text-sm text-muted-foreground truncate" title={fileTargets[0].name}>{fileTargets[0].name}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Filing is firm-wide: once marked, it clears for everyone. Where did it go? (optional)
          </p>
          <input
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void confirmFile(); } }}
            maxLength={500}
            placeholder="e.g. SmartVault / 2026 Tax"
            className={`${inputClass} w-full`}
          />
          <DialogFooter>
            <button
              onClick={closeFileDialog}
              className="px-3 py-1.5 rounded-md text-sm border border-border hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => void confirmFile()}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
              Mark filed
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* === END DOCUMENTS TAB === */

/* === COMMENTS TAB === */

function CommentRow({
  item,
  onMarkRead,
}: {
  item: PortalActivityItem;
  onMarkRead: (item: PortalActivityItem) => void;
}) {
  const goToTask = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (item.read || item.type !== "comment") return;
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href")!;
    try {
      await api.markPortalActivityRead([{ type: "comment", id: item.id, account_id: item.account_id }]);
    } catch { /* navigate anyway */ }
    window.location.href = href;
  };

  return (
    <div className={`flex items-start gap-3 px-4 py-3.5 hover:bg-muted/30 transition-colors ${item.read ? "" : "bg-amber-50/50"}`}>
      {/* unread dot */}
      <button
        onClick={() => onMarkRead(item)}
        disabled={item.read}
        title={item.read ? "Read" : "Mark as read"}
        aria-label={item.read ? "Read" : "Mark as read"}
        className="mt-1.5 shrink-0 w-2.5 h-2.5 flex items-center justify-center disabled:cursor-default"
      >
        <span className={`block w-2 h-2 rounded-full ${item.read ? "bg-transparent" : "bg-amber-500"}`} />
      </button>

      <div className="mt-0.5 shrink-0 text-muted-foreground">
        <MessageSquare className="w-4 h-4" />
      </div>

      {/* task on line 1, comment on line 2 */}
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className={`text-sm leading-snug truncate ${item.read ? "font-semibold text-foreground/80" : "font-bold text-foreground"}`}>{item.task_title}</p>
        <p className="text-sm font-medium text-foreground/70 leading-snug truncate">{item.name}</p>
      </div>

      <div className="flex items-center gap-1.5 shrink-0 ml-2">
        <span className="text-xs text-muted-foreground w-16 text-right" title={new Date(item.created_at).toLocaleString()}>
          {formatRelative(item.created_at)}
        </span>
        <a
          href={`/accounts/${item.account_id}?openProject=${item.project_id ?? ""}&openTask=${item.task_id ?? ""}`}
          onClick={goToTask}
          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          Go to task
        </a>
      </div>
    </div>
  );
}

// Groups a flat list of comments by project, most recent group first.
function groupByProject(items: PortalActivityItem[]): [string, PortalActivityItem[]][] {
  const map = new Map<string, PortalActivityItem[]>();
  items.forEach((item) => {
    const key = item.project_id ?? "none";
    const g = map.get(key) ?? [];
    g.push(item);
    map.set(key, g);
  });
  return [...map.entries()].sort(([, a], [, b]) =>
    new Date(b[0].created_at).getTime() - new Date(a[0].created_at).getTime()
  );
}

function CommentsTab({
  items,
  onCountChange,
}: {
  items: PortalActivityItem[];
  onCountChange: (n: number) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const refetchPortalActivity = useRefetchPortalActivity();
  const [accountFilter, setAccountFilter] = useState<string>("all");
  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<string>>(new Set());

  const comments = useMemo(
    () => items
      .filter((i) => i.type === "comment")
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [items],
  );

  const patchRead = (predicate: (i: PortalActivityItem) => boolean) => {
    queryClient.setQueryData<PortalActivityItem[]>(PAGE_KEY, (prev) =>
      prev?.map((i) => (predicate(i) ? { ...i, read: true } : i))
    );
  };

  const markRead = (item: PortalActivityItem) => {
    if (item.read || item.type !== "comment") return;
    patchRead((i) => i.type === "comment" && i.id === item.id);
    api.markPortalActivityRead([{ type: "comment", id: item.id, account_id: item.account_id }])
      .then(refetchPortalActivity)
      .catch(() => {
        toast({ title: "Couldn't mark as read", variant: "destructive" });
        refetchPortalActivity();
      });
  };

  // Server-side this marks every unread item on the account (uploads too), as it always has.
  const markAccountRead = (accountId: string) => {
    patchRead((i) => i.account_id === accountId && i.type !== "document");
    api.markAccountPortalActivityRead(accountId)
      .then(refetchPortalActivity)
      .catch(() => {
        toast({ title: "Couldn't mark all as read", variant: "destructive" });
        refetchPortalActivity();
      });
  };

  const toggleAccount = (accountId: string) => {
    setCollapsedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId); else next.add(accountId);
      return next;
    });
  };

  const accounts = useMemo(() => {
    const map = new Map<string, string>();
    comments.forEach((i) => map.set(i.account_id, i.account_name));
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [comments]);

  const filtered = useMemo(
    () => (accountFilter === "all" ? comments : comments.filter((i) => i.account_id === accountFilter)),
    [comments, accountFilter],
  );

  useEffect(() => { onCountChange(filtered.length); }, [filtered.length, onCountChange]);

  const grouped = useMemo(() => {
    const map = new Map<string, PortalActivityItem[]>();
    filtered.forEach((item) => {
      const group = map.get(item.account_id) ?? [];
      group.push(item);
      map.set(item.account_id, group);
    });
    return [...map.entries()].sort(([, a], [, b]) =>
      new Date(b[0].created_at).getTime() - new Date(a[0].created_at).getTime()
    );
  }, [filtered]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm font-medium text-foreground shrink-0">Filter by account:</label>
        <select
          value={accountFilter}
          onChange={(e) => { setAccountFilter(e.target.value); setCollapsedAccounts(new Set()); }}
          className="text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-[220px]"
        >
          <option value="all">All accounts ({comments.length})</option>
          {accounts.map(([id, name]) => (
            <option key={id} value={id}>
              {name} ({comments.filter((i) => i.account_id === id).length})
            </option>
          ))}
        </select>
      </div>

      {filtered.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-sm font-medium text-foreground">No client comments yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Messages clients leave on portal tasks will appear here.</p>
        </div>
      )}

      <div className="space-y-3">
        {grouped.map(([accountId, accountItems]) => {
          const isCollapsed = collapsedAccounts.has(accountId);
          const accountName = accountItems[0].account_name;
          const accountUnread = accountItems.filter((i) => !i.read).length;
          return (
            <div key={accountId} className="border border-border rounded-lg overflow-hidden bg-card shadow-sm">
              {/* Account header */}
              <button
                onClick={() => toggleAccount(accountId)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/50 hover:bg-muted transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-foreground">{accountName}</span>
                  {accountUnread > 0 && (
                    <span className="text-xs font-semibold bg-amber-400 text-amber-900 rounded-full px-2 py-0.5">
                      {accountUnread} new
                    </span>
                  )}
                  <span className="text-xs font-medium text-muted-foreground">{accountItems.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">{formatRelative(accountItems[0].created_at)}</span>
                  {accountUnread > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); markAccountRead(accountId); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); markAccountRead(accountId); } }}
                      className="text-xs font-medium text-primary hover:underline cursor-pointer"
                    >
                      Mark all read
                    </span>
                  )}
                  <a
                    href={`/accounts/${accountId}?tab=portal`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Open account →
                  </a>
                  <span className="text-muted-foreground text-xs">{isCollapsed ? "▼" : "▲"}</span>
                </div>
              </button>

              {/* Comments grouped by project */}
              {!isCollapsed && (
                <div className="divide-y divide-border">
                  {groupByProject(accountItems).map(([projectId, projectItems]) => {
                    const first = projectItems[0];
                    return (
                      <div key={projectId}>
                        <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border/60">
                          <span className="text-xs font-bold text-foreground uppercase tracking-wide truncate">{first.project_title}</span>
                          <ProjectBadge type={first.project_type} taxYear={first.project_tax_year} />
                          <span className="ml-auto flex items-center gap-1.5 shrink-0">
                            {projectItems.some((i) => !i.read) && (
                              <span className="text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-1.5 py-0.5">
                                {projectItems.filter((i) => !i.read).length} new
                              </span>
                            )}
                            <span className="text-[10px] font-semibold text-muted-foreground">{projectItems.length}</span>
                          </span>
                        </div>
                        {projectItems.map((item) => (
                          <CommentRow key={itemKey(item)} item={item} onMarkRead={markRead} />
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* === END COMMENTS TAB === */

type PageTab = "documents" | "comments";

const readTabFromUrl = (): PageTab =>
  new URLSearchParams(window.location.search).get("tab") === "comments" ? "comments" : "documents";

export default function ClientResponses() {
  const [tab, setTabState] = useState<PageTab>(readTabFromUrl);
  const [visibleCount, setVisibleCount] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>("");

  const { data: items = [], isLoading } = useQuery<PortalActivityItem[]>({
    queryKey: PAGE_KEY,
    queryFn: () => fetchWithAuth<PortalActivityItem[]>("/api/portal-activity?include=documents&limit=1000"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Mirror the tab into ?tab= (keeping any other params) so links can target either tab.
  const setTab = (next: PageTab) => {
    setTabState(next);
    const params = new URLSearchParams(window.location.search);
    params.set("tab", next);
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : ""));
  };

  const openDocCount = useMemo(() => items.filter((i) => isFilable(i) && !i.filed).length, [items]);
  const unreadCommentCount = useMemo(() => items.filter((i) => i.type === "comment" && !i.read).length, [items]);

  const openPreview: OpenPreview = (url, name) => {
    setPreviewName(name);
    setPreviewUrl(url);
  };

  const tabButton = (value: PageTab, label: string, badge: number) => (
    <button
      onClick={() => setTab(value)}
      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        tab === value ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
      {badge > 0 && <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-amber-400 text-amber-900">{badge}</span>}
    </button>
  );

  const countLabel = tab === "documents"
    ? `${visibleCount} document${visibleCount !== 1 ? "s" : ""}`
    : `${visibleCount} comment${visibleCount !== 1 ? "s" : ""}`;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-foreground">Portal Responses</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Documents clients send through the portal and document-request links, and client comments on portal tasks.
            </p>
          </div>
          {!isLoading && <span className="text-sm font-medium text-muted-foreground">{countLabel}</span>}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border mb-4">
          {tabButton("documents", "Documents", openDocCount)}
          {tabButton("comments", "Comments", unreadCommentCount)}
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : tab === "documents" ? (
          <DocumentsTab items={items} onPreview={openPreview} onCountChange={setVisibleCount} />
        ) : (
          <CommentsTab items={items} onCountChange={setVisibleCount} />
        )}
      </div>

      {/* Preview modal */}
      {previewUrl && (
        <Dialog open={true} onOpenChange={() => { setPreviewUrl(null); setPreviewName(""); }}>
          <DialogContent className="max-w-4xl w-full" style={{ height: "88vh", display: "flex", flexDirection: "column" }}>
            <DialogHeader>
              <DialogTitle className="truncate text-sm font-semibold">{previewName}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden rounded">
              {IMAGE_NAME.test(previewName) ? (
                <div className="w-full h-full flex items-center justify-center bg-muted/30 overflow-auto">
                  <img src={previewUrl} alt={previewName} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <iframe src={previewUrl} title={previewName} className="w-full h-full border-0" />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
}
